import { defineStore } from 'pinia';

import { UserRole } from '@tallo/user';

import { ActivateRenterAccountWithGoogleDto } from './model/activate-renter-account-with-google.dto';
import { CreateUserDto } from './model/create-user.dto';
import { PasswordSetupDto } from './model/password-setup.dto';
import { SetupNewPasswordDto } from './model/setup-new-password.dto';
import { SignInResultDto } from './model/sign-in-result.dto';
import { SignInWithCredsDto } from './model/sign-in-with-creds.dto';
import authService from './services/auth.service';
import googleSignInService, { GoogleRenderButtonOptions } from './services/google-sign-in.service';
import marketingSignUpParamsService from './services/marketing-sign-up-params.service';
import tokenService from './services/token.service';

export const useAuthStore = defineStore('auth', {
  state: (): {
    isAuthenticated: boolean;
    /** Challenge from a sign-in that hit the 2FA gate; exchanged for a token by signInWithTwoFactorCode */
    pendingTwoFactorToken: string | null;
    /** Where the user was headed when sign-in (or its 2FA gate) intervened; resumed once they are in */
    postSignInRedirect: string | null;
  } => ({
    isAuthenticated: false,
    pendingTwoFactorToken: null,
    postSignInRedirect: null,
  }),
  getters: {
    isTwoFactorPending: (state): boolean => Boolean(state.pendingTwoFactorToken),
  },
  actions: {
    setupAuthenticatedFlag() {
      this.isAuthenticated = tokenService.getToken() !== null;
    },

    authenticate(accessToken: string): void {
      if (!accessToken) {
        return;
      }

      this.isAuthenticated = true;
      this.pendingTwoFactorToken = null;
      tokenService.saveToken(accessToken);
    },

    /**
     * Abandons an in-progress 2FA challenge (e.g. the user goes back to sign in). The remembered
     * destination stays: a fresh sign-in should still land on the link they came from.
     */
    cancelTwoFactor(): void {
      this.pendingTwoFactorToken = null;
    },

    rememberPostSignInRedirect(path: string): void {
      this.postSignInRedirect = path;
    },

    /** Returns and clears the remembered destination; every sign-in completion path calls this */
    consumePostSignInRedirect(): string | null {
      const redirect = this.postSignInRedirect;
      this.postSignInRedirect = null;
      return redirect;
    },

    /**
     * Either completes the sign-in or parks the 2FA challenge for the code screen.
     * Returns true when a second factor is still required.
     */
    handleSignInResult(result: SignInResultDto): boolean {
      if (result.twoFactorRequired) {
        this.pendingTwoFactorToken = result.twoFactorToken ?? null;
        return true;
      }

      this.authenticate(result.accessToken!);
      return false;
    },

    async signInWithTwoFactorCode(code: string): Promise<void> {
      if (!this.pendingTwoFactorToken) {
        throw new Error('No two-factor sign-in in progress');
      }

      const { accessToken } = await authService.signInWithTwoFactor(this.pendingTwoFactorToken, code);
      this.authenticate(accessToken);
    },

    logout(): void {
      // Best-effort server-side revoke; local cleanup must not wait on (or fail with) the network.
      // Gated on isAuthenticated so a 401 on this very call (which triggers the interceptor's
      // logout again) cannot loop.
      if (this.isAuthenticated) {
        authService.signOutCurrentSession().catch(() => undefined);
      }

      tokenService.removeToken();
      this.isAuthenticated = false;
      this.pendingTwoFactorToken = null;
      googleSignInService.signOut();
    },

    initializeGoogleSignInClient(): void {
      googleSignInService.initialize();
    },

    renderGoogleSignInButton(params: GoogleRenderButtonOptions): void {
      googleSignInService.renderGoogleSignInButton(params);
    },

    async signInWithCredentials(params: SignInWithCredsDto): Promise<{ twoFactorRequired: boolean }> {
      const result = await authService.signInWithCredentials(params);

      return { twoFactorRequired: this.handleSignInResult(result) };
    },

    async sendPasswordResetLink(email: string, forRole: UserRole): Promise<void> {
      await authService.sendPasswordResetLink(email, forRole);
    },

    async confirmPasswordReset(passwordSetupDto: PasswordSetupDto): Promise<{ twoFactorRequired: boolean }> {
      const result = await authService.confirmPasswordReset(passwordSetupDto);

      return { twoFactorRequired: this.handleSignInResult(result) };
    },

    async requestOneTimeSignInCode(email: string): Promise<void> {
      await authService.requestOneTimeSignInCode(email);
    },

    async signInWithOneTimeCode(email: string, code: number): Promise<{ twoFactorRequired: boolean }> {
      const result = await authService.signInWithOneTimeCode(email, code);

      return { twoFactorRequired: this.handleSignInResult(result) };
    },

    async investorSignUpWithCreds(userData: CreateUserDto): Promise<{ userId: string }> {
      const marketingParams = marketingSignUpParamsService.getUtmParams();

      return authService.investorSignUpWithCreds({
        user: userData,
        marketingParams: marketingParams || null,
      });
    },

    async investorConfirmEmail(userId: string, code: number): Promise<{ companyId: string }> {
      const { companyId, accessToken } = await authService.investorConfirmEmail(userId, code);

      this.authenticate(accessToken);

      return { companyId };
    },

    async investorResendEmailConfirmationCode(userId: string): Promise<void> {
      await authService.investorResendEmailConfirmationCode(userId);
    },

    async investorSignInWithGoogle(
      googleJwt: string,
    ): Promise<{ companyId: string | null; twoFactorRequired: boolean }> {
      const result = await authService.investorSignInWithGoogle(googleJwt);

      return { companyId: result.companyId, twoFactorRequired: this.handleSignInResult(result) };
    },

    async investorSignUpWithGoogle(
      googleJwt: string,
    ): Promise<{ companyId: string | null; twoFactorRequired: boolean }> {
      const marketingParams = marketingSignUpParamsService.getUtmParams();
      const result = await authService.investorSignUpWithGoogle({
        googleJwt,
        marketingParams: marketingParams || null,
      });

      return { companyId: result.companyId, twoFactorRequired: this.handleSignInResult(result) };
    },

    async renterSignInWithGoogle(googleJwt: string): Promise<{ twoFactorRequired: boolean }> {
      const result = await authService.renterSignInWithGoogle(googleJwt);

      return { twoFactorRequired: this.handleSignInResult(result) };
    },

    async renterActivateWithGoogle(accountActivationDto: ActivateRenterAccountWithGoogleDto): Promise<void> {
      const { accessToken } = await authService.renterActivateWithGoogle(accountActivationDto);

      this.authenticate(accessToken);
    },

    async renterActivateAccount(passwordSetupDto: SetupNewPasswordDto): Promise<void> {
      const { accessToken } = await authService.renterActivateWithCreds(passwordSetupDto);

      this.authenticate(accessToken);
    },
  },
});
